import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  ALERT_CENTER_NS,
  LEAD_AGENT_SURFACES,
} from 'src/alerts/AlertCenter.constants';
import {
  acknowledgmentOutlivesEpisode,
  arbitrateWide,
  canAcknowledge,
  formatElapsedWait,
  resolveAlertPlanNameKey,
  resolveAlertTierNameKey,
  selectAlerts,
  selectLiveEpisodeKeys,
  withPaymentZoneStrip,
} from 'src/alerts/AlertCenter.functions';
import type {
  tAlertSnapshot,
  tBoardExpiredSignal,
  tAlertSurface,
  tLiveAlert,
  tTrialCapAlertVariant,
  tWapiDisconnectCause,
} from 'src/alerts/AlertCenter.types';
import { gateMetaPricingNoticeOnWallet } from 'src/alerts/MetaPricingNotice.functions';
import {
  formatPaymentDateLong,
  resolveSurface,
} from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import { formatPlanPauseDate } from 'src/functions/planPause.functions';
import { resolveTrialCapBannerView } from 'src/components/LeadAgentTrialCapBanner/LeadAgentTrialCapBanner.functions';
import { formatInvoiceAmount } from 'src/components/LeadAgentTransferPendingBanner/LeadAgentTransferPendingBanner.functions';
import { useLeadAgentPendingPaymentState } from 'src/components/LeadAgentTransferPendingBanner/useLeadAgentPendingPayment.hook';
import { RouterPaths } from 'src/components/Router/Router.constants';
import { VITE_PAST_DUE_LADDER_FEATURE } from 'src/constants/envs';
import { useUserAvailability } from 'src/hooks/availability/useUserAvailability.hook';
import { useWapiNeverConnected } from 'src/hooks/whatsapp/useWapiNeverConnected.hook';
import { useWhatsAppWebAccountStatus } from 'src/hooks/whatsapp/useWhatsAppWebAccountStatus.hook';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useWalletSurfaceAccess } from 'src/pages/Wallet/hooks/useWalletSurfaceAccess';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums';
import { useIntercomContext } from 'src/providers/IntercomProvider/useIntercomContext.hook';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { eBillingMigrationEventType } from 'src/providers/SocketsProvider/Sockets.enums';
import type { TBillingMigrationEvent } from 'src/providers/SocketsProvider/Sockets.types';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import {
  canSeeWapiDisconnectedBanner,
  isTrialSubscription,
} from 'src/providers/SubscriptionProvider/Subscription.utils';
import { resolveWapiActivation } from 'src/pages/WhatsappApi/hooks/useWapiNotConnected';
import { resolveWapiDisconnection } from 'src/alerts/WapiDisconnection.functions';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { AlertCenterContext } from './AlertCenter.context';
import { resolvePastDueSnapshot } from './PastDueSnapshot.functions';
import {
  acknowledgeKeyInStorage,
  getAcknowledgmentStorageKey,
  pruneKeysInStorage,
  readAcknowledgedKeys,
  readAcknowledgments,
} from './AlertCenter.storage';
import {
  useCalendarConnectionSource,
  useMetaTokenInvalidNoticeSource,
  useQuotaExceededCountSource,
  useSellerStatusSource,
  useLeadAgentCapRaiseNoticeSource,
  useWorkspaceNoticesSource,
} from './AlertCenter.sources';
import { applyAlertSnapshotOverride } from './AlertSnapshotOverride.functions';
import {
  selectLoadingAlertIds,
  takeConfirmedResolutions,
  trackVisibleAlerts,
  type tAlertSessionEntry,
  type tTrackedAlert,
} from './AlertSessionLog.functions';
import type {
  tAlertCenterContext,
  tInlineAlertRegistration,
  tStageThresholdEditRequest,
} from './AlertCenter.types';
import { useAlertSnapshotOverride } from './useAlertSnapshotOverride.hook';
import { useWideAlertShowsCta } from './useWideAlertShowsCta.hook';

/**
 * RET-1871 — narrows an `unknown` value to what `new Date(...)` accepts,
 * without an assertion: the simulator's `rawInputs.subscriptionEndDate` is
 * deliberately `unknown`-typed so it can carry adversarial values (e.g. the
 * string `'not-a-date'`), and this guard is what lets the real parse below
 * run against it safely at compile time.
 */
function isDateConstructorInput(
  value: unknown,
): value is string | number | Date {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date
  );
}

/**
 * Turns the integrations row's `reason` into the disconnect cause the alert
 * branches on.
 *
 * The cause rides on the row itself, so it is known exactly when the row is —
 * there is no window in which the channel is known to be down while the cause
 * is still unresolved.
 *
 * A cause the row leaves unstated, or one this app does not recognise, resolves
 * to `'error'`. The cause no longer selects a remedy — every retired account is
 * offered the same self-serve reconnect — so it is kept for analytics and for
 * any future cause whose remedy is not the reconnect.
 */
function resolveWapiDisconnectCause(reason?: string): tWapiDisconnectCause {
  if (reason === 'REMOVED_BY_META') return 'REMOVED_BY_META';
  return reason === 'DISCONNECTED' ? 'DISCONNECTED' : 'error';
}

/** Consumer hook. Mirrors `useLeadAgentAcceptanceContext`. */
export function useAlertCenterContext(): tAlertCenterContext {
  return useContext(AlertCenterContext);
}

/**
 * THE surface resolver for the Alert Center (RET-1876; closes the RET-1875
 * review nit). The provider used to run two mapping mechanisms — the legacy
 * `resolveSurface` for the snapshot and a bare `pathname.startsWith` check for
 * the quota-count fetch scope — which could drift apart silently. One resolver
 * now answers both. `chat` extends the vocabulary for fetch-scope gating only:
 * no declaration lists it in `surfaces`, so selection behaves exactly as when
 * the Messenger resolved to null.
 */
export function resolveAlertSurface(pathname: string): tAlertSurface | null {
  if (pathname.startsWith(RouterPaths.chat)) return 'chat';
  return resolveSurface(pathname);
}

/** Stable empty selection for the flag-off state — a fresh `[]` per render
 *  would bust every consumer memoized on `alerts`. */
const EMPTY_ALERTS: tLiveAlert[] = [];

/**
 * Assembles `tAlertSnapshot` from live context and hands it to the pure
 * `selectAlerts`. Every field below reproduces the gate of the component that
 * owns the alert TODAY — the parity requirement. Where the mapping is not a
 * straight read, the divergence and the reason are commented inline; those
 * comments are load-bearing, since the rollout has no feature flag behind
 * which a mis-ported gate could hide.
 */
export function useAlertCenterValue(): tAlertCenterContext {
  const location = useLocation();
  // The LANGUAGE drives the invoice amount format — its CURRENCY comes from
  // the backend; mixing the two showed a Brazilian customer a Mexican-
  // formatted amount (RET-1764). `t` resolves the F6 plan display names from
  // the alertCenter namespace (RET-1877): the snapshot's `planName` is a
  // LOCALIZED string, never `getPlanName()`'s hardcoded English.
  const { t: tAlertCopy, i18n } = useTranslation(ALERT_CENTER_NS);
  const acl = useACLContext();
  const { currentUserId, currentUserRole } = useUserContext();
  const { currentWorkspaceId, currentWorkspace } = useWorkspaceContext();
  const {
    subscription,
    isClient,
    isSetUpAvailable,
    setUpInfo,
    // U11 (RET-1269) — the RESOLVED past-due counter, not
    // `subscription.daysPastDue`. The provider already picked between the
    // published value and U8's rollout fallback; re-deriving it here would
    // open a second definition of "day 5" that could drift from the one the
    // rest of the product counts on.
    daysPastDue: resolvedDaysPastDue,
    // R10 (RET-1269) — the subscription refetch, for the return-to-tab
    // refresh below. The only automatic `getSummary` this app has otherwise
    // is `Subscription.hook`'s workspace-change effect.
    getSummary,
  } = useSubscriptionContext();
  const { whatsappBusinessIntegration, isHydrated } = useIntegrationsContext();
  const { accountHealthStatus, violationType } = useWhatsappApiContext();
  const { methods: intercomMethods } = useIntercomContext();
  const leadAgent = useLeadAgentAcceptanceContext();
  // M3/D2 (RET-1875) — the umbrella flag gates every request this provider
  // owns; off = inert (the snapshot still assembles from contexts that are
  // already live for the legacy banners, but zero fetches are issued that
  // main does not issue today). The gate is applied HERE, at the provider,
  // not in each consumer — and it reads the same snapshotted expression the
  // legacy suppression will read in Unit 5, so show-new and suppress-legacy
  // can never diverge (divergent-guard-sources lesson).
  const { isAlertCenterEnabled, isPastDuePaymentZoneEnabled } =
    useFeatureFlagsContext();
  const { errorCode } = useWhatsAppWebAccountStatus({
    // The hook hydrates over REST on mount; an undefined workspace disables
    // it entirely, which is the flag-off "zero new traffic" contract.
    workspaceId: isAlertCenterEnabled ? currentWorkspaceId : undefined,
  });
  const { isActive, isLoaded } = useUserAvailability();
  const {
    billingMigrationStartedEvent,
    billingMigrationFinishedEvent,
    catchUpEvent,
  } = useSocketsContext();
  // RET-1871 — dev-only simulator override; always `null` outside
  // `IS_LOCAL_DEV`, so every seam below is a no-op in a deployed build.
  const override = useAlertSnapshotOverride();
  const isOverrideActive = override !== null;

  // BUG-438 (compound learning) — socket-driven UI needs a refetch fallback.
  // On reconnection catch-up (socket back, or tab resumed) every enabled
  // fetch-backed source re-runs, so a P0 resolved during the offline window
  // clears without waiting for the next focus/event, and a new one appears.
  const [sourcesRefetchToken, setSourcesRefetchToken] = useState(0);
  catchUpEvent.useSubscription(() => {
    setSourcesRefetchToken((previous) => previous + 1);
  });

  const workspaceId = currentWorkspaceId ?? null;
  // Called once and depended on as a BOOLEAN: `canUserAccessWhatsAppApi` is a
  // fresh closure on every ACL render, so depending on the function identity
  // would bust the snapshot memo on every unrelated context change.
  const canAccessWhatsAppApi = acl.canUserAccessWhatsAppApi();
  const role = currentUserRole ?? null;
  const surface = resolveAlertSurface(location.pathname);
  // RET-2452 (R11) — published by the overview while it renders the past-due
  // payment zone. NOT derived from the route: the tab changes without the URL.
  const [isPastDueZoneOnScreen, setPastDueZoneOnScreen] = useState(false);

  // ── Quota (TopDescription inline <Alert> + ActiveLeadsQuotaBanner) ────────
  // The two twins collapse into one declaration. The LIMIT is passed raw:
  // `AlertCenter.functions` carries the divide-by-zero guard that only the
  // chat twin has today, so the safer of the two behaviours wins. The funnel
  // twin's `count >= Number(limit) * 0.8` is true for EVERY count when the
  // limit is 0/NaN, which is exactly the false at-limit banner the guard was
  // written to prevent.
  const activeLeadsLimit = Number(acl.activeLeadsLimit);
  const activeLeadsCount = acl.activeLeadsCount;
  const isQuotaEligiblePlan =
    acl.isGrowthPlan || acl.isBusinessPlan || acl.isAdvancedPlan;
  // U4 — drives the quota CTA's destination (add-on modal vs plans view),
  // the same branch the legacy banners take.
  const isLeadLimitAddonEligiblePlan = acl.isBusinessPlan || acl.isAdvancedPlan;
  const hasUsableLimit =
    Number.isFinite(activeLeadsLimit) && activeLeadsLimit > 0;
  const isQuotaAtLimit =
    isQuotaEligiblePlan &&
    hasUsableLimit &&
    activeLeadsCount >= activeLeadsLimit;

  // ── WhatsApp API ─────────────────────────────────────────────────────────
  // One gate for both WAPI alerts: only Professional and Advanced include the
  // channel, and neither alert may read the slice before it hydrates. Shared
  // rather than repeated so a future change to eligibility cannot reach one
  // alert and miss the other.
  const isWapiAlertEligible =
    isHydrated && canSeeWapiDisconnectedBanner(subscription);

  // INT-1653 — which disconnection notice the row calls for. Some answers
  // change with time alone (inactivity shows 5 minutes after the event, 8a
  // turns into 8b at 10, 8c stops being news at 24 h), so the resolver says
  // when to ask again and a timer bumps this clock then.
  const [wapiNoticeClock, setWapiNoticeClock] = useState(() => Date.now());
  const wapiResolution = useMemo(
    () =>
      isWapiAlertEligible
        ? resolveWapiDisconnection({
            state: whatsappBusinessIntegration.state,
            reason: whatsappBusinessIntegration.reason,
            metaReason: whatsappBusinessIntegration.metaReason,
            metaInitiatedBy: whatsappBusinessIntegration.metaInitiatedBy,
            disconnectedAt: whatsappBusinessIntegration.disconnectedAt,
            connectionMode: whatsappBusinessIntegration.connectionMode,
            offboardedAt: whatsappBusinessIntegration.offboardedAt,
            reconnectionOutcome: whatsappBusinessIntegration.reconnectionOutcome,
            // Never earlier than the last tick, never stale after a row change.
            now: Math.max(Date.now(), wapiNoticeClock),
          })
        : { disconnection: null, recheckAt: null },
    [isWapiAlertEligible, whatsappBusinessIntegration, wapiNoticeClock],
  );
  const wapiRecheckAt = wapiResolution.recheckAt;
  useEffect(() => {
    if (wapiRecheckAt === null) return undefined;
    const timer = window.setTimeout(
      () => setWapiNoticeClock(Date.now()),
      Math.max(0, wapiRecheckAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [wapiRecheckAt]);
  const wapiDisconnection = wapiResolution.disconnection;

  // A DISCONNECTED row whose notice is still on hold (inactivity, first 5
  // minutes) is not raised yet: the phone often comes back by itself.
  const isWapiRowDisconnected =
    whatsappBusinessIntegration.state === eIntegrationStatus.DISCONNECTED;
  const wapiDisconnected =
    isWapiAlertEligible &&
    isWapiRowDisconnected &&
    !(wapiDisconnection === null && wapiRecheckAt !== null);

  // The Chat empty state invites the same workspace to connect, so the
  // never-connected derivation lives in the shared hook and this alert reads
  // it rather than keeping a second copy that could drift.
  const wapiNoAccount = useWapiNeverConnected();

  // INT-1516 — the activation verdict (held, failed). Same hydration gate and
  // plan gate as the two siblings above, PLUS trial workspaces: a trial can
  // connect a number, and two of the dead coex connects the plan names are
  // trials. The verdict comes through the resolver every other surface reads,
  // and the row's `reason` is deliberately not read here: it is an activation
  // reason, never a disconnect cause.
  const isWapiActivationAlertEligible =
    isHydrated &&
    (isTrialSubscription(subscription) ||
      canSeeWapiDisconnectedBanner(subscription));
  const wapiActivation = resolveWapiActivation(whatsappBusinessIntegration.state);
  const wapiActivationPending =
    isWapiActivationAlertEligible && wapiActivation === 'pending';
  const wapiActivationFailed =
    isWapiActivationAlertEligible && wapiActivation === 'failed';

  // Gated on hydration ALONE, unlike its siblings above, which also require
  // plan or trial eligibility. Those are connection alerts about a channel
  // Basic and Legacy plans do not have; a routing conflict is a fact about a
  // WABA row that exists and is losing this workspace its messages, so the
  // plan it sits on changes nothing — which is why the chat empty state
  // explains the same fact ungated, and why this keeps one answer across
  // surfaces. A row-presence gate would be inert: when bff-web drops the
  // WhatsApp API row the slice keeps its default and the flag reads false, so
  // absence already fails closed.
  const wapiRoutingConflict =
    isHydrated && Boolean(whatsappBusinessIntegration.inboundRoutingConflict);

  // ── Fetch-backed sources ─────────────────────────────────────────────────
  // `enabled` mirrors each banner's FULL legacy gate: the predicate AND the
  // mount scope (F2 — the first port kept only the predicate, so seller-status
  // fetched wherever a seller navigated and quota-exceeded-count on every
  // private page; that was new traffic, not a port). Restored scopes:
  // seller-status only on the surfaces its banner mounted (Dashboard 'home' /
  // Pipe 'funnels'), the quota count only inside the Messenger, where its
  // banner lived. On other pages each source resolves to its ready-null state
  // and the alert simply does not fire there — the same parity the legacy
  // mount gave. On top of that, every source is ANDed with the Alert Center
  // flag (M3): flag off = this provider issues zero requests.
  const isOnSellerBannerSurface = surface === 'home' || surface === 'funnels';
  const sellerStatus = useSellerStatusSource({
    workspaceId,
    // RET-1871 — a real fetch racing a simulated value would overwrite it.
    enabled:
      isAlertCenterEnabled &&
      acl.userIsSeller &&
      isOnSellerBannerSurface &&
      !isOverrideActive,
    refetchToken: sourcesRefetchToken,
  });
  const sellerPausedValue =
    sellerStatus.status === 'ready'
      ? sellerStatus.value?.isPaused === true
      : null;
  const sellerPaused = useMemo(
    () => ({ status: sellerStatus.status, value: sellerPausedValue }),
    [sellerStatus.status, sellerPausedValue],
  );
  const ownerName = sellerStatus.value?.ownerName ?? null;
  // RET-1891 — analytics-only, so it rides the context rather than the
  // snapshot: it gates nothing, so it has no place in `tAlertSnapshot`.
  const sellerPauseReason = sellerStatus.value?.pauseReason || null;

  /**
   * R13b — the occurrence segment of an alert key. Keyed on the subscription
   * PERIOD END, not the plan name: a plan name is stable across periods, so an
   * acknowledgment keyed on it would never expire and the alert would never
   * come back.
   */
  // RET-1871 — the raw-input seam: `rawInputs.subscriptionEndDate` stands in
  // for `subscription.endDate` when set, so the simulator can reproduce the
  // malformed-date crash class at the point it actually occurred (before the
  // snapshot exists), not just its post-hoc `billingPeriodId` result.
  const rawEndDateOverride = override?.rawInputs?.subscriptionEndDate;
  const effectiveEndDate: unknown =
    rawEndDateOverride !== undefined
      ? rawEndDateOverride
      : subscription?.endDate;
  const billingPeriodId = useMemo(() => {
    // `subscription.endDate` is typed `Date` and built at the provider
    // boundary with `new Date(summaryData.endDate)`, so a malformed API value
    // arrives here as an Invalid Date — which is TRUTHY. Calling
    // `.toISOString()` on it throws `RangeError` inside this memo, during
    // render, in a provider that wraps every private page: a white screen, not
    // a missing alert. Validate before formatting.
    const periodEnd = isDateConstructorInput(effectiveEndDate)
      ? new Date(effectiveEndDate)
      : null;
    if (periodEnd && !Number.isNaN(periodEnd.getTime())) {
      return periodEnd.toISOString().slice(0, 10);
    }
    // Not every quota-eligible workspace exposes a period end (legacy plans,
    // a subscription still loading). Falling back to the current month keeps
    // the alert dismissible for those users while still making the key
    // expire: an acknowledgment under a null period would have no occurrence
    // segment at all, and once persistence lands that is permanent — the
    // exact defect R13b exists to prevent. A month is finer than or comparable
    // to the real billing period — roughly equal for a monthly plan (just
    // misaligned), far finer for an annual one — so it can only expire sooner
    // than the truth, never later. Bounded expiry is the property that matters.
    // The `-fallback` marker is deliberate and documented on `buildAlertKey`:
    // a bare `YYYY-MM-01` could collide with a genuine period end on that day.
    return `${new Date().toISOString().slice(0, 7)}-fallback`;
  }, [effectiveEndDate]);
  // Null while we do not know: the row is the single source for both the
  // predicate above and the cause, so neither can be read before it hydrates.
  const wapiDisconnectCause = wapiDisconnected
    ? resolveWapiDisconnectCause(whatsappBusinessIntegration.reason)
    : null;
  // AGI-352 — the calendar connection state, fetched only where its alert can
  // render (the Lead Agent surfaces) and only for the roles that receive it —
  // the F2 rule: the fetch scope mirrors the declaration's own scope, so no
  // page issues a request whose answer could never be shown to this viewer.
  const isOnLeadAgentSurface =
    surface !== null && LEAD_AGENT_SURFACES.includes(surface);
  const calendarConnectionRevoked = useCalendarConnectionSource({
    workspaceId,
    enabled:
      isAlertCenterEnabled &&
      (acl.userIsOwner || acl.userIsAdmin) &&
      isOnLeadAgentSurface &&
      !isOverrideActive,
    refetchToken: sourcesRefetchToken,
  });

  /*
   * AGI-428: the Meta-pricing notice. Gated on the flag, the audience and the
   * simulator, and on NOTHING ELSE: unlike the calendar source above there is
   * no surface scope to mirror, because the declaration carries no `surfaces`
   * (the notice is due on whatever page the owner lands on) and the automatic
   * modal has to be able to open there too.
   *
   * SELLERs never issue the request. That is the F2 rule (the fetch scope
   * mirrors the declaration's own audience), and here it also happens to be
   * the privacy answer: the payload is this workspace's spend.
   */
  const metaPricingNoticeSource = useWorkspaceNoticesSource({
    workspaceId,
    enabled:
      isAlertCenterEnabled &&
      (acl.userIsOwner || acl.userIsAdmin) &&
      !isOverrideActive,
    refetchToken: sourcesRefetchToken,
  });

  /*
   * AGI-428, message 3 (6-oct): "tus creditos ahora son saldo" only for a
   * workspace that sees the balance. The SAME predicate the wallet surfaces
   * and the credits-to-balance notice open on (RET-2325), read here once so
   * the snapshot, the bell, the detail and the auto-open host all see the
   * same filtered notice. Messages 1 and 2 pass through untouched.
   */
  const { isWalletSurfaceVisible, isWalletRouteDecidable } =
    useWalletSurfaceAccess();
  const metaPricingNotice = useMemo(
    () =>
      gateMetaPricingNoticeOnWallet(metaPricingNoticeSource, {
        isWalletSurfaceVisible,
        isWalletRouteDecidable,
      }),
    [metaPricingNoticeSource, isWalletSurfaceVisible, isWalletRouteDecidable],
  );

  /**
   * INT-1522: same gating as its pricing sibling above -- OWNER/ADMIN only
   * (SELLERs cannot act on a dead Meta connection), off while the dev
   * simulator overrides the snapshot.
   */
  const metaTokenInvalidNotice = useMetaTokenInvalidNoticeSource({
    workspaceId,
    enabled:
      isAlertCenterEnabled &&
      (acl.userIsOwner || acl.userIsAdmin) &&
      !isOverrideActive,
    refetchToken: sourcesRefetchToken,
  });

  /*
   * RET-2191: the Lead Agent cap-raise notice. Owner and admin only, like its
   * declaration (F2: the fetch scope mirrors the audience); a seller has no
   * plan to be told about.
   */
  const leadAgentCapRaiseNotice = useLeadAgentCapRaiseNoticeSource({
    workspaceId,
    enabled:
      isAlertCenterEnabled &&
      (acl.userIsOwner || acl.userIsAdmin) &&
      !isOverrideActive,
    refetchToken: sourcesRefetchToken,
  });

  // Same resolver as the snapshot's `surface` — the two scopes cannot drift.
  const isOnQuotaCountSurface = surface === 'chat';
  const quotaExceededCount = useQuotaExceededCountSource({
    workspaceId,
    enabled:
      isAlertCenterEnabled &&
      isQuotaAtLimit &&
      isOnQuotaCountSurface &&
      !isOverrideActive,
    refetchToken: sourcesRefetchToken,
  });
  // Read through the SHARED store the two Lead Agent banners already use
  // (RET-1764) rather than a fourth source: two independent reads can
  // disagree, and the failure mode there is a customer who owes money and is
  // at the token cap seeing NO money alert at all. F1 — the store now
  // publishes its REAL status: 'loading' during a workspace's first fetch is
  // what lets the R3b hold actually fire, so a capped workspace with a
  // pending invoice resolves to ONE alert instead of flickering
  // capped→transfer on cold load. Flag off passes undefined, which both
  // disables the store subscription (zero traffic) and resolves 'ready'.
  const pendingPayment = useLeadAgentPendingPaymentState(
    isAlertCenterEnabled ? currentWorkspace?.id : undefined,
  );
  const leadAgentInvoiceAmount =
    pendingPayment.payment.pending && pendingPayment.payment.currency
      ? formatInvoiceAmount(
          pendingPayment.payment.amountCents ?? 0,
          pendingPayment.payment.currency,
          i18n.language,
        )
      : null;
  const leadAgentInvoiceDueDate = pendingPayment.payment.pending
    ? formatPaymentDateLong(pendingPayment.payment.dueDate ?? null)
    : null;
  // U4 — the transfer alert's CTA is the hosted invoice link, with the same
  // https guard the legacy banner applies before rendering its anchor.
  const rawInvoiceUrl = pendingPayment.payment.pending
    ? pendingPayment.payment.invoiceUrl
    : null;
  const leadAgentInvoiceUrl =
    typeof rawInvoiceUrl === 'string' && rawInvoiceUrl.startsWith('https://')
      ? rawInvoiceUrl
      : null;
  // A pending invoice whose URL is absent or fails the guard makes the CTA
  // and the acknowledge control withdraw silently (R13c) — correct for the
  // user, invisible to us. Log it once per offending value so billing can be
  // chased instead of discovered through "no puedo pagar" tickets.
  const warnedInvoiceUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingPayment.payment.pending || leadAgentInvoiceUrl !== null) return;
    const offending = rawInvoiceUrl ?? '<missing>';
    if (warnedInvoiceUrlRef.current === offending) return;
    warnedInvoiceUrlRef.current = offending;
    LLog.warn(
      {
        component: LOG_COMPONENTS.SUBSCRIPTION,
        method: 'useAlertCenterValue',
        operation: LOG_OPERATIONS.DEBUG,
      },
      'Pending Lead Agent invoice without a usable https URL — transfer alert degrades to no CTA',
      { workspaceId, invoiceUrl: offending },
    );
  }, [
    pendingPayment.payment.pending,
    rawInvoiceUrl,
    leadAgentInvoiceUrl,
    workspaceId,
  ]);

  // ── Past due / mora (U11, RET-1269) ──────────────────────────────────────
  /**
   * Resolved every render, then given a stable identity keyed on its VALUE, so
   * the snapshot below spreads it and depends on it as ONE entry. The thirteen
   * names used to be written out three times — here, in the snapshot, in its
   * dependency array — so a new field cost three edits and forgetting the third
   * produced a silently stale snapshot (the `SNAPSHOT_KEYS` drift this file
   * warns about, minus the parity test that catches it).
   *
   * 🔴 Do not "simplify" this into a `useMemo` over the resolver's inputs. That
   * breaks twice: `resolvePastDueSnapshot` reads the WALL CLOCK
   * (`pastDueCancellationPassed`), so input-memoized it freezes at `false` for
   * the life of the mount and the ladder keeps stating a cancellation date
   * already gone by; and `subscription` is an object whose identity the provider
   * does not guarantee, so depending on it re-runs this memo on unrelated
   * churn — a guarantee the old thirteen primitives had for free, and which the
   * memoization tests in this hook's suite enforce.
   *
   * `JSON.stringify` keeps both: the slice is all primitives in a fixed literal
   * order, so the key is a faithful value identity and the object changes only
   * when a field's value does, clock included.
   */
  const resolvedPastDue = resolvePastDueSnapshot({
    subscription,
    resolvedDaysPastDue,
    isLadderEnabled: VITE_PAST_DUE_LADDER_FEATURE,
  });
  const pastDueValueKey = JSON.stringify(resolvedPastDue);
  const pastDue = useMemo(
    () => resolvedPastDue,
    // Intentionally the key and not the object: see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pastDueValueKey],
  );
  // The rejection pair feeds the logging effect below, NOT the snapshot:
  // spreading it in would widen `tAlertSnapshot` by two keys and break the ten
  // factories. Pulled out here so `pastDueFields` is exactly the thirteen the
  // snapshot takes.
  const {
    invoiceUrlRejectionReason,
    invoiceUrlRejectionHost,
    ...pastDueFields
  } = pastDue;
  // A refused payment link makes the ladder's CTA withdraw silently — correct
  // for the customer, invisible to us. Logged once per offending value so
  // billing can be chased instead of discovered through "no puedo pagar"
  // tickets, exactly like the Lead Agent guard above.
  //
  // ⚠️ The REJECTION is logged, never the URL: a refused value can itself be a
  // live bearer link to a payable invoice, so only the reason and the hostname
  // travel — never the path, the query, or any credentials. There is also no
  // log for an ABSENT url: a past-due workspace whose `openInvoiceSource` is
  // `none` has nothing to chase, and logging it would bury the real
  // rejections.
  const warnedPastDueUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (invoiceUrlRejectionReason === null) return;
    const fingerprint = `${invoiceUrlRejectionReason}:${
      invoiceUrlRejectionHost ?? '<unparsed>'
    }`;
    if (warnedPastDueUrlRef.current === fingerprint) return;
    warnedPastDueUrlRef.current = fingerprint;
    LLog.warn(
      {
        component: LOG_COMPONENTS.SUBSCRIPTION,
        method: 'useAlertCenterValue',
        operation: LOG_OPERATIONS.DEBUG,
      },
      'Past-due invoice URL refused by the Stripe origin guard — the ladder degrades to no payment CTA',
      {
        workspaceId,
        reason: invoiceUrlRejectionReason,
        host: invoiceUrlRejectionHost,
      },
    );
  }, [invoiceUrlRejectionReason, invoiceUrlRejectionHost, workspaceId]);

  /**
   * ── The return-to-tab refresh (R10, RET-1269) ────────────────────────────
   *
   * 🔴 THE PAST-DUE LADDER CLEARS ITSELF WHEN THE PAYMENT LANDS, AND THIS IS
   * THE ONLY THING THAT MAKES THAT TRUE.
   *
   * `SubscriptionProvider` has no poll: `getSummary` runs on mount, on
   * workspace change and on explicit mutations. The hosted invoice opens in a
   * NEW TAB, so the customer paying is something that happens entirely off
   * this document and there is no callback to hang a refetch on — the return
   * to this tab is the only signal there is. It is armed unconditionally
   * rather than only after a click, because the owner may equally have paid
   * from the invoice email on their phone.
   *
   * 🔴 IT LIVES HERE, NOT IN `PastDueFinalDetail`, AND THAT IS THE POINT.
   *
   * The detail used to own it, which put the only payment signal inside the
   * dialog an owner closes the moment Stripe opens in front of it: the X,
   * Escape and a backdrop click all reach `AlertDetailModal`'s `requestClose`,
   * whose only guard is the nested-modal one this renderer never sets. The
   * non-dismissible day 9+ strip then went on announcing a cancellation that
   * may already have been paid until the workspace changed or the page
   * reloaded — the exact false dunning signal the ladder exists to remove,
   * reintroduced through the dialog's own close affordance. This provider
   * outlives every dialog (it wraps the whole private shell in
   * `Providers.component`), so the refresh is alive whether the detail is
   * open, closed, or never opened at all.
   *
   * Gated on BOTH the past-due slice and the umbrella flag: a workspace that
   * owes nothing has nothing to reconcile, and flag-off is inert by contract —
   * with no ladder there is no detail either, so this must issue no request
   * main does not issue today.
   */
  const isPastDueLive = isAlertCenterEnabled && pastDue.subscriptionPastDue;
  // Through a ref so the listener is armed ONCE per past-due episode:
  // `getSummary` is a fresh closure on every SubscriptionProvider render, and
  // depending on its identity would tear down and re-add the listener on every
  // render of a provider that re-renders on every context it reads.
  const getSummaryRef = useRef(getSummary);
  getSummaryRef.current = getSummary;
  // A tab switched back and forth quickly would otherwise queue one summary
  // per transition; the provider tolerates it, but there is no reason to ask.
  const isRefetchingSummaryRef = useRef(false);
  useEffect(() => {
    if (!isPastDueLive) return undefined;
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      if (isRefetchingSummaryRef.current) return;
      isRefetchingSummaryRef.current = true;
      void Promise.resolve(getSummaryRef.current())
        // Swallowed on purpose: this is a background reconciliation the
        // customer never asked for. A failed one leaves the notice exactly as
        // it was, which is the honest fallback — and the next return retries.
        .catch(() => {})
        .finally(() => {
          isRefetchingSummaryRef.current = false;
        });
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPastDueLive]);

  // ── Billing migration (TopDescription inline <Alert>) ─────────────────────
  const [isBillingMigrationInProgress, setIsBillingMigrationInProgress] =
    useState(false);
  billingMigrationStartedEvent.useSubscription(
    (data: TBillingMigrationEvent) => {
      if (data.eventType === eBillingMigrationEventType.STARTED) {
        setIsBillingMigrationInProgress(true);
      }
    },
  );
  billingMigrationFinishedEvent.useSubscription(
    (data: TBillingMigrationEvent) => {
      if (data.eventType === eBillingMigrationEventType.FINISHED) {
        setIsBillingMigrationInProgress(false);
      }
    },
  );

  // ── Lead Agent ───────────────────────────────────────────────────────────
  const leadAgentExpired =
    leadAgent.isExpiredForOwner || leadAgent.isExpiredForAdmin;
  // KTD9 — the provider already folds the PERSISTED dismissal into
  // `isExpiredForOwner`. An admin cannot dismiss and today's admin banner
  // ignores `dismissedAt` entirely, so passing it through unconditionally
  // would newly hide the ADMIN alert. Scoped to the role that can dismiss.
  const leadAgentDismissedAt = acl.userIsOwner
    ? (leadAgent.dismissedAt ?? null)
    : null;
  // The shutdown banner also refuses to render without a formattable cut-off
  // date — an unparseable `nextPaymentAt` hides it today, because its copy is
  // the date. Computed once and reused as the interpolation value.
  const leadAgentShutdownDate = formatPaymentDateLong(
    leadAgent.acceptance?.nextPaymentAt ?? null,
  );
  const leadAgentPausedWithinTrial =
    leadAgent.isPausedWithinTrialForOwner && leadAgentShutdownDate !== null;
  // Resolved ONCE and read twice. The predicate and the copy have to agree on
  // which of the four situations is live: derived separately, a second call
  // could land on a different variant than the one that made the alert fire.
  const trialCapView = resolveTrialCapBannerView({
    isTrialSightingForOwner: leadAgent.isTrialSightingForOwner,
    isTrialSightingForAdmin: leadAgent.isTrialSightingForAdmin,
    isTrialCappedForOwner: leadAgent.isTrialCappedForOwner,
    isTrialCappedForAdmin: leadAgent.isTrialCappedForAdmin,
    isTrialExtendedCappedForOwner: leadAgent.isTrialExtendedCappedForOwner,
    isTrialExtendedCappedForAdmin: leadAgent.isTrialExtendedCappedForAdmin,
  });
  const leadAgentTrialCapReached = trialCapView !== null;
  // One-to-one with the provider now that the last-days variants are gone.
  const leadAgentTrialCapVariant: tTrialCapAlertVariant | null =
    trialCapView?.variant ?? null;

  // The usage alerts key off a percentage, but the banner they replace keys
  // off the provider's usage PHASE — and that phase is not a pure threshold:
  // `resolveUsagePhase` also requires a cap to exist (`hasCap`) and treats a
  // paused agent as capped regardless of consumption. Feeding the raw percent
  // would fire the alert for a workspace with no cap at all. Deriving the
  // percent FROM the phase (clamped into that phase's band) keeps the gate
  // exact; inside `nearLimit` the real value is already in [80, 100).
  const isUsageCapped =
    leadAgent.isCappedForOwner || leadAgent.isCappedForAdmin;
  const isUsageNearLimit =
    leadAgent.isNearLimitForOwner || leadAgent.isNearLimitForAdmin;
  const rawCapacityPct = leadAgent.usageCapacityPct;
  let leadAgentCapacityPercent: number | null = null;
  if (isUsageCapped) {
    leadAgentCapacityPercent = Math.max(rawCapacityPct ?? 100, 100);
  } else if (isUsageNearLimit) {
    leadAgentCapacityPercent = Math.min(Math.max(rawCapacityPct ?? 80, 80), 99);
  }

  // ── Plan ─────────────────────────────────────────────────────────────────
  // The promo banner renders today when the workspace is not a client AND a
  // set-up (first purchase) is available, so "has an active plan" is the
  // negation of exactly that pair.
  const hasActivePlan = isClient || !isSetUpAvailable;
  const planCode = subscription?.planCode ?? null;
  // F6 — resolved BY LOCALE, and only for known codes: an unknown billing
  // code degrades to null (the copy resolvers render without a plan name)
  // rather than surfacing a raw identifier to the user.
  const planNameKey = planCode ? resolveAlertPlanNameKey(planCode) : null;
  const planName = planNameKey ? tAlertCopy(planNameKey) : null;

  // F6 for the tier, same shape as the plan name above: the DISPLAY name goes
  // into the snapshot, never the billing identifier. Interpolating the raw
  // `suggestedTier` is what put "GROWTH" in the acceptance body next to
  // "Growth" in its own offer line.
  const leadAgentTierCode =
    leadAgent.acceptance?.suggestedTier ?? leadAgent.usageTier ?? null;
  const leadAgentTierKey = leadAgentTierCode
    ? resolveAlertTierNameKey(leadAgentTierCode)
    : null;
  const leadAgentTierName = leadAgentTierKey
    ? tAlertCopy(leadAgentTierKey)
    : null;

  // KTD7 — the board's red-card signal. Held here rather than derived because
  // the producer (the Pipe page) mounts BELOW this provider, and re-deriving it
  // from a fetch would re-query exactly what the open board already holds.
  const [boardExpired, setBoardExpired] = useState<tBoardExpiredSignal>({
    hasExpired: false,
  });
  // Stable across renders: boards keep it in an effect dependency array, and an
  // identity that changed every render would loop the publish effect.
  const reportBoardExpiredLead = useCallback((signal: tBoardExpiredSignal) => {
    setBoardExpired((previous) =>
      isSameBoardExpiredSignal(previous, signal) ? previous : signal,
    );
  }, []);

  // The explainer names the wait in the reader's language, resolved here for
  // the reason `planName` is: the registry's copy resolvers are pure and have
  // no `t` to word it with.
  const boardExpiredElapsed = boardExpired.hasExpired
    ? formatElapsedWait(boardExpired.elapsedMinutes, {
        language: i18n.language,
        t: tAlertCopy,
      })
    : null;

  // U9 (KTD6) — the return leg of the same bridge. The explainer's CTA has no
  // route to navigate to: its destination is the stage modal the board owns.
  // The request is parked here, where both the banner on the board and the
  // bell can reach it, and the mounted board picks it up.
  const [stageThresholdEditRequest, setStageThresholdEditRequest] =
    useState<tStageThresholdEditRequest | null>(null);
  const stageThresholdRequestIdRef = useRef(0);
  // The reported signal is read through a ref so the seam keeps ONE identity:
  // it sits in the context value, and a new function on every red-card change
  // would re-render every alert surface for nothing.
  const boardExpiredRef = useRef(boardExpired);
  boardExpiredRef.current = boardExpired;
  const requestStageThresholdEdit = useCallback(() => {
    const signal = boardExpiredRef.current;
    // Nothing red means no stage to name. Silently doing nothing beats opening
    // the modal on a stage the user never asked about.
    if (!signal.hasExpired) return;
    stageThresholdRequestIdRef.current += 1;
    setStageThresholdEditRequest({
      stageId: signal.stageId,
      requestId: stageThresholdRequestIdRef.current,
    });
  }, []);
  const consumeStageThresholdEditRequest = useCallback(() => {
    setStageThresholdEditRequest((previous) => (previous ? null : previous));
  }, []);

  const snapshot = useMemo<tAlertSnapshot>(
    () => ({
      workspaceId,
      role,
      surface,
      pastDueZoneOnScreen: isPastDueZoneOnScreen,
      pastDuePaymentZoneEnabled: isPastDuePaymentZoneEnabled,

      activeLeadsCount,
      activeLeadsLimit,
      isQuotaEligiblePlan,
      isLeadLimitAddonEligiblePlan,
      canAccessWhatsAppApi,
      hasActivePlan,
      planName,
      planPauseActive: subscription.planPause?.inPauseWindow === true,
      planPauseEndsAt: subscription.planPause?.endsAt
        ? formatPlanPauseDate({
            instant: subscription.planPause.endsAt,
            locale: i18n.language,
            style: 'long',
          }) || null
        : null,

      wapiDisconnected,
      wapiDisconnectCause,
      wapiDisconnection,
      wapiNoAccount,
      wapiActivationPending,
      wapiActivationFailed,
      wapiRoutingConflict,
      whatsAppWebErrorCode: errorCode ?? null,
      accountHealthStatus: accountHealthStatus ?? null,
      // Parity with AccountHealthBanner: the violation table is only consulted
      // for a meta_violation-sourced HIGH_SPAMMING. A count-based
      // HIGH_SPAMMING carries no violation_type and keeps the generic copy.
      accountHealthViolation:
        accountHealthStatus === 'HIGH_SPAMMING'
          ? (violationType ?? null)
          : null,

      leadAgentExpired,
      leadAgentDismissedAt,
      leadAgentPausedWithinTrial,
      leadAgentAcceptancePending: leadAgent.isBannerVisible,
      // Urgency and its countdown ride along with the pending flag, straight
      // from the acceptance provider — the same values the legacy banner reads,
      // so the two surfaces cannot disagree about how urgent "urgent" is. Null
      // when nothing is pending, so no stale countdown survives the resolution.
      leadAgentAcceptanceUrgency: leadAgent.isBannerVisible
        ? leadAgent.bannerVariant
        : null,
      leadAgentDaysUntilPayment: leadAgent.isBannerVisible
        ? leadAgent.daysUntilPayment
        : null,
      leadAgentPaymentDate: leadAgent.isBannerVisible
        ? formatPaymentDateLong(leadAgent.acceptance?.nextPaymentAt ?? null)
        : null,
      leadAgentTrialCapReached,
      leadAgentTrialCapVariant,
      leadAgentTrialActivatable:
        leadAgent.isTrialActivatableForOwner ||
        leadAgent.isTrialActivatableForAdmin,
      leadAgentCapacityPercent,
      leadAgentDaysSinceExpiry: leadAgent.daysSincePayment,
      leadAgentTier: leadAgentTierName,

      // F1 — real status threaded through (was hardcoded 'ready', which made
      // the R3b hold unreachable and the flicker guarantee aspirational).
      pendingPayment: {
        status: pendingPayment.status,
        value: pendingPayment.payment.pending,
      },
      sellerPaused,
      calendarConnectionRevoked,
      metaPricingNotice,
      metaTokenInvalidNotice,
      leadAgentCapRaiseNotice,

      userInactive: isLoaded && !isActive,
      isBillingMigrationInProgress,

      // All thirteen past-due fields, from the one memoized slice. Adding a
      // field to `resolvePastDueSnapshot` lands here with no edit at all.
      ...pastDueFields,

      boardHasExpiredLead: boardExpired.hasExpired,
      boardExpiredStageId: boardExpired.hasExpired
        ? boardExpired.stageId
        : null,
      boardExpiredStageName: boardExpired.hasExpired
        ? boardExpired.stageName
        : null,
      boardExpiredLeadName: boardExpired.hasExpired
        ? boardExpired.leadName
        : null,
      boardExpiredElapsed,

      // ── Interpolation inputs ─────────────────────────────────────────────
      billingPeriodId,
      ownerName,
      leadAgentShutdownDate,
      leadAgentInvoiceAmount,
      leadAgentInvoiceDueDate,
      leadAgentInvoiceUrl,
      leadAgentTrialDaysRemaining: leadAgent.trialCapDaysUntilTrialEnd,
      subscriptionPromoTotal:
        setUpInfo?.trialCost === undefined || setUpInfo?.trialCost === null
          ? null
          : String(setUpInfo.trialCost),
      subscriptionPromoCurrency: setUpInfo?.currency ?? null,
      hiddenConversationsCount: quotaExceededCount,
    }),
    [
      workspaceId,
      role,
      surface,
      isPastDueZoneOnScreen,
      isPastDuePaymentZoneEnabled,
      activeLeadsCount,
      activeLeadsLimit,
      isQuotaEligiblePlan,
      isLeadLimitAddonEligiblePlan,
      canAccessWhatsAppApi,
      hasActivePlan,
      planName,
      wapiDisconnected,
      wapiDisconnectCause,
      wapiDisconnection,
      wapiNoAccount,
      wapiActivationPending,
      wapiActivationFailed,
      wapiRoutingConflict,
      errorCode,
      accountHealthStatus,
      violationType,
      leadAgentExpired,
      leadAgentDismissedAt,
      leadAgentPausedWithinTrial,
      leadAgent.isBannerVisible,
      leadAgentTrialCapReached,
      leadAgentTrialCapVariant,
      leadAgent.isTrialActivatableForOwner,
      leadAgent.isTrialActivatableForAdmin,
      leadAgentCapacityPercent,
      leadAgent.daysSincePayment,
      leadAgentTierName,
      pendingPayment.status,
      pendingPayment.payment.pending,
      sellerPaused,
      calendarConnectionRevoked,
      metaPricingNotice,
      metaTokenInvalidNotice,
      leadAgentCapRaiseNotice,
      isLoaded,
      isActive,
      isBillingMigrationInProgress,
      // ONE entry for the whole past-due slice, and it is `pastDue` rather than
      // the `pastDueFields` the body spreads: the rest-destructure above builds
      // a FRESH object every render, so depending on it would bust this memo
      // every render — the exact cost the old field-by-field list was paying to
      // avoid. `pastDue` is the memoized source both derive from, so it changes
      // exactly when one of the thirteen fields does.
      pastDue,
      boardExpired,
      boardExpiredElapsed,
      billingPeriodId,
      ownerName,
      leadAgentShutdownDate,
      leadAgentInvoiceAmount,
      leadAgentInvoiceDueDate,
      leadAgentInvoiceUrl,
      leadAgent.trialCapDaysUntilTrialEnd,
      setUpInfo?.trialCost,
      setUpInfo?.currency,
      quotaExceededCount,
    ],
  );

  // RET-1871 — the snapshot-patch seam: applied AFTER the real snapshot is
  // assembled, so a patch overlays only the fields it names and everything
  // else (real workspace state) passes through untouched. A `null` override
  // is the identity — zero behaviour change when the gate is off.
  const patchedSnapshot = useMemo(
    () => applyAlertSnapshotOverride(snapshot, override),
    [snapshot, override],
  );
  // RET-1875 — flag off = the provider is inert end to end: selection yields
  // the empty set, so EVERY consumer (bell, slot, the sidebar's contextual
  // strip, any future surface) renders nothing without needing its own gate.
  // Gating the one derivation instead of each consumer is what keeps a
  // forgotten gate from ever shipping a surface half-on. RET-1871
  // integration: an ACTIVE dev override also enables delivery, so the
  // simulator keeps working on a flag-off local workspace; in deployed
  // builds the override is always null and the flag alone decides.
  const areSurfacesEnabled = isAlertCenterEnabled || isOverrideActive;
  // ── Acknowledgment (D6, RET-1877: persisted per workspace+user) ──────────
  // The store moved from provider state to `AlertCenter.storage.ts`; this
  // hook keeps only a version counter and derives the key list by reading
  // storage in render (a pure, GC-filtered read — the Mount-Gating lesson
  // forbids lazy-useState hydration here because `workspaceId` resolves late,
  // and a memo read re-runs on that change instead of clobbering anything).
  // Before both ids resolve there is nothing user-scoped to read, so the
  // pre-hydration state is simply "no acknowledgments" — same as today's
  // cold start. The workspace-switch reset falls out of the same read: each
  // workspace+user pair is its own bag.
  const [acknowledgmentVersion, bumpAcknowledgmentVersion] = useReducer(
    (version: number) => version + 1,
    0,
  );
  const userId = currentUserId ?? null;
  // The session overlay: acknowledged key → its instant (epoch ms), which the
  // feed's history shows (AGI-891).
  const [sessionAcknowledgments, setSessionAcknowledgments] = useState<
    Record<string, number>
  >({});
  // Mirrored into a ref so the pruning effect can read the CURRENT overlay
  // without depending on it — depending would re-run the pruner after its own
  // setState for nothing.
  const sessionAcknowledgmentsRef = useRef(sessionAcknowledgments);
  sessionAcknowledgmentsRef.current = sessionAcknowledgments;
  const { acknowledgedKeys, acknowledgedAt } = useMemo(() => {
    // The version counter invalidates this read after every local mutation
    // and every cross-tab storage event.
    void acknowledgmentVersion;
    const stored =
      workspaceId && userId
        ? readAcknowledgments({ workspaceId, userId })
        : {};
    // Storage can refuse writes (private mode, quota): the session overlay is
    // what keeps an acknowledgment working for THIS session anyway. Keys the
    // pruner removed are dropped from the overlay, so it never resurrects one.
    // A stored instant wins: it is the one a reload shows.
    const merged = new Set([
      ...Object.keys(stored),
      ...Object.keys(sessionAcknowledgments),
    ]);
    return {
      acknowledgedKeys: Array.from(merged),
      acknowledgedAt: { ...sessionAcknowledgments, ...stored },
    };
  }, [workspaceId, userId, acknowledgmentVersion, sessionAcknowledgments]);

  /*
   * Después de `acknowledgedKeys` a propósito: la selección las necesita.
   *
   * No para filtrar —eso sigue pasando abajo, en `alerts`— sino para quitarle
   * a un aviso ya confirmado el PODER DE SUPRIMIR a otros. Antes la supresión
   * corría antes de saber qué estaba confirmado, así que `la-transfer-pending`
   * seguía tapando `la-usage-capped`, que es un P0 no descartable: confirmar
   * un P1 lavaba un P0 fuera de la vista (revisión adversaria, 2026-09-02).
   *
   * `derivedAlerts` sigue conteniendo al aviso confirmado, porque
   * `AlertDetailModal` juzga su vigencia contra esta lista sin filtrar.
   */
  const derivedAlerts = useMemo(
    () =>
      areSurfacesEnabled
        ? selectAlerts(patchedSnapshot, undefined, acknowledgedKeys)
        : EMPTY_ALERTS,
    [areSurfacesEnabled, patchedSnapshot, acknowledgedKeys],
  );

  // AGI-888 (U3) — this session's acknowledgments and confirmed resolutions,
  // per workspace and user, until reload (see `tAlertSessionEntry`).
  const [alertSessionLog, setAlertSessionLog] = useState<tAlertSessionEntry[]>(
    [],
  );

  const acknowledgeAlert = useCallback(
    (alert: tLiveAlert) => {
      // R13c — an alert whose CTA resolves to nothing for this role is not
      // acknowledgeable BY that role, or an admin clears an owner-only
      // revenue alert out of their view with nothing routing it onward.
      if (!role || !canAcknowledge(alert, role)) return;
      if (workspaceId && userId) {
        acknowledgeKeyInStorage({ workspaceId, userId }, alert.key);
      }
      setSessionAcknowledgments((previous) =>
        alert.key in previous
          ? previous
          : { ...previous, [alert.key]: Date.now() },
      );
      bumpAcknowledgmentVersion();
      // AGI-888 — the acknowledgment enters the session log once, with the
      // copy the person was reading, wherever it was made (feed or bell).
      const entry: tAlertSessionEntry = {
        kind: 'acknowledged',
        key: alert.key,
        id: alert.id,
        priority: alert.priority,
        copy: alert.copy,
        at: Date.now(),
      };
      setAlertSessionLog((previous) =>
        previous.some(
          (logged) => logged.kind === 'acknowledged' && logged.key === alert.key,
        )
          ? previous
          : [...previous, entry],
      );
    },
    [role, workspaceId, userId],
  );

  // Belt-and-braces on scope change: alert keys embed the workspace, so a
  // leaked overlay entry could never match another workspace's alerts — this
  // just keeps the session overlay from accumulating across switches.
  useEffect(() => {
    setSessionAcknowledgments((previous) =>
      Object.keys(previous).length === 0 ? previous : {},
    );
  }, [workspaceId, userId]);

  // ── Episode pruning (D6) ───────────────────────────────────────────────
  // An alert whose predicate verifiably turned false ends its episode: drop
  // the acknowledgment so a later re-fire is not silently pre-dismissed
  // (BUG-719 — a dismissal mutes the episode, not the alert). Three guards
  // distinguish this from the session-scoped predecessor, which pruned
  // against `derivedAlerts`:
  // 1. Judged on `selectLiveEpisodeKeys` (surface/exclusivity/suppression
  //    ignored) — with persistence, pruning the surface-filtered set would
  //    turn every navigation into a loss of acknowledgments.
  // 2. Only keys OBSERVED alive this session are prunable. A cold load with
  //    half-hydrated sources must not wipe the persisted bag.
  // 3. Uncertainty (loading source, throwing predicate) defers judgment.
  // 4. R15 (U9) — a declaration may opt OUT of pruning entirely. The board's
  //    red-card signal turns false on every unmount, so the first-red
  //    explainer's episode "ends" the moment the user leaves the funnel;
  //    pruning there would hand the dismissal back and show it again on the
  //    next visit. Per declaration, never a change of default.
  const episodeLedgerRef = useRef<{
    scope: string;
    /** Alert key → declaration id, for keys seen alive this session. */
    keys: Map<string, string>;
  }>({ scope: '', keys: new Map() });
  useEffect(() => {
    const scope = `${workspaceId ?? ''}:${userId ?? ''}`;
    if (episodeLedgerRef.current.scope !== scope) {
      episodeLedgerRef.current = { scope, keys: new Map() };
    }
    const ledger = episodeLedgerRef.current.keys;
    const episodes = selectLiveEpisodeKeys(patchedSnapshot);
    episodes.liveKeys.forEach((declarationId, key) => {
      ledger.set(key, declarationId);
    });

    // The session overlay is judged alongside storage: an acknowledgment that
    // never reached disk (no scope yet, refused write) still ends with its
    // episode.
    const candidates = new Set(Object.keys(sessionAcknowledgmentsRef.current));
    if (workspaceId && userId) {
      readAcknowledgedKeys({ workspaceId, userId }).forEach((key) =>
        candidates.add(key),
      );
    }
    const ended = Array.from(candidates).filter((key) => {
      const declarationId = ledger.get(key);
      if (!declarationId) return false; // never seen alive — don't judge
      if (episodes.liveKeys.has(key)) return false; // still alive
      if (episodes.uncertainIds.has(declarationId)) return false; // unknown
      if (acknowledgmentOutlivesEpisode(declarationId)) return false; // R15
      return true; // observed alive, now verifiably ended
    });
    if (ended.length === 0) return;
    ended.forEach((key) => ledger.delete(key));
    if (workspaceId && userId) {
      pruneKeysInStorage({ workspaceId, userId }, ended);
    }
    setSessionAcknowledgments((previous) => {
      const kept = Object.entries(previous).filter(
        ([key]) => !ended.includes(key),
      );
      return kept.length === Object.keys(previous).length
        ? previous
        : Object.fromEntries(kept);
    });
    bumpAcknowledgmentVersion();
  }, [patchedSnapshot, workspaceId, userId]);

  // Cross-tab convergence: the `storage` event fires in every OTHER tab when
  // one tab writes, which is exactly the set that needs to re-read.
  useEffect(() => {
    if (!workspaceId || !userId) return undefined;
    const storageKey = getAcknowledgmentStorageKey({ workspaceId, userId });
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) bumpAcknowledgmentVersion();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [workspaceId, userId]);

  const alerts = useMemo(() => {
    if (acknowledgedKeys.length === 0) return derivedAlerts;
    return derivedAlerts.filter(
      (alert) => !acknowledgedKeys.includes(alert.key),
    );
  }, [derivedAlerts, acknowledgedKeys]);

  // ── Session log of resolutions (AGI-888, U3) ─────────────────────────────
  // Every alert the viewer has seen alive this session is tracked; when a later
  // snapshot CONFIRMS its episode is over (predicate false with its sources
  // ready, now and when last seen), it enters the log as resolved. The
  // provider is always mounted, so this holds while the feed is not: the
  // last-resolved row says «Se resolvió…» for a P0 that resolved elsewhere.
  // A source that goes loading or fails confirms nothing.
  const sessionTrackingRef = useRef<{
    scope: string;
    tracked: Map<string, tTrackedAlert>;
  }>({ scope: '', tracked: new Map() });
  useEffect(() => {
    const scope = `${workspaceId ?? ''}:${userId ?? ''}`;
    const isNewScope = sessionTrackingRef.current.scope !== scope;
    if (isNewScope) sessionTrackingRef.current = { scope, tracked: new Map() };
    const { tracked } = sessionTrackingRef.current;

    const resolved = takeConfirmedResolutions({
      tracked,
      snapshot: patchedSnapshot,
      at: Date.now(),
    });
    trackVisibleAlerts({ tracked, alerts, snapshot: patchedSnapshot });

    if (isNewScope) {
      // A new workspace or user starts its own log; a switch is a reload
      // today, so this only covers the late-resolving ids.
      setAlertSessionLog((previous) =>
        previous.length === 0 && resolved.length === 0 ? previous : resolved,
      );
      return;
    }
    if (resolved.length > 0) {
      setAlertSessionLog((previous) => [...previous, ...resolved]);
    }
  }, [alerts, patchedSnapshot, workspaceId, userId]);

  // R3b, published (AGI-888): what cannot be judged yet, so a surface that
  // freezes its list on mount can tell "not known yet" from "not live".
  const loadingAlertIds = useMemo(
    () => selectLoadingAlertIds(patchedSnapshot),
    [patchedSnapshot],
  );

  // ── Wide arbitration (RET-1876, D4) ──────────────────────────────────────
  // One wide critical per viewer; the losers are the bell's "waiting" list.
  // Runs on the post-acknowledgment set for consistency, though P0s are never
  // acknowledgeable (R13). Pure and synchronous: when the winner's predicate
  // resolves, the next-in-line is promoted in the SAME render pass (G12) —
  // no dwell, no intermediate empty frame.
  //
  // RET-2452 (R11) — the strip is the WINNER only. `waiting` is the bell's list
  // and keeps the alert as `selectAlerts` made it, so Ajustes › Suscripción
  // rewords the strip and nothing else (`withPaymentZoneStrip`).
  const stripPastDueZoneOnScreen = patchedSnapshot.pastDueZoneOnScreen;
  const stripPaymentZoneEnabled = patchedSnapshot.pastDuePaymentZoneEnabled;
  const wideArbitration = useMemo(() => {
    const arbitration = arbitrateWide(alerts);
    if (!arbitration.winner) return arbitration;
    const winner = withPaymentZoneStrip({
      alert: arbitration.winner,
      snapshot: {
        pastDueZoneOnScreen: stripPastDueZoneOnScreen,
        pastDuePaymentZoneEnabled: stripPaymentZoneEnabled,
      },
    });
    return winner === arbitration.winner
      ? arbitration
      : { ...arbitration, winner };
  }, [alerts, stripPastDueZoneOnScreen, stripPaymentZoneEnabled]);
  // AGI-885 — one primary per screen: whether that winner's card carries a
  // button right now, read by the in-page surface in the same render.
  const wideAlertShowsCta = useWideAlertShowsCta(wideArbitration.winner);

  // ── Inline-visibility channel ────────────────────────────────────────────
  // A surface that renders an alert itself registers it here; the badge count
  // subtracts the registered set. Without an owner at this level the demotion
  // result lives in a component the panel cannot see.
  const [inlineRegistrations, setInlineRegistrations] = useState<
    Record<string, tInlineAlertRegistration>
  >({});
  const tokenRef = useRef(0);

  const registerInlineAlert = useCallback(
    (alertId: string, source?: string) => {
      tokenRef.current += 1;
      const token = `${alertId}#${tokenRef.current}`;
      setInlineRegistrations((previous) => ({
        ...previous,
        [token]: { id: alertId, source: source ?? null },
      }));
      return () => {
        setInlineRegistrations((previous) => {
          if (!(token in previous)) return previous;
          const next = { ...previous };
          delete next[token];
          return next;
        });
      };
    },
    [],
  );

  const inlineAlertIds = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(inlineRegistrations).map(
            (registration) => registration.id,
          ),
        ),
      ),
    [inlineRegistrations],
  );

  // Two surfaces can render the SAME declaration under different analytics
  // sources ('funnel' and 'chat' for the quota alert). Collapsing the two
  // components into one declaration must not collapse that value, so both
  // are kept per alert id.
  const inlineAlertSources = useMemo(() => {
    const bySource: Record<string, string[]> = {};
    Object.values(inlineRegistrations).forEach((registration) => {
      if (!registration.source) return;
      const list = bySource[registration.id] ?? [];
      if (!list.includes(registration.source)) list.push(registration.source);
      bySource[registration.id] = list;
    });
    return bySource;
  }, [inlineRegistrations]);

  const badgeCount = useMemo(
    () => alerts.filter((alert) => !inlineAlertIds.includes(alert.id)).length,
    [alerts, inlineAlertIds],
  );

  // Held in a ref so the seam identity survives Intercom re-renders: a new
  // `openSupport` on every render would change the whole context value and
  // re-render every alert surface for nothing.
  const intercomMethodsRef = useRef(intercomMethods);
  intercomMethodsRef.current = intercomMethods;
  const openSupport = useCallback((message?: string) => {
    intercomMethodsRef.current?.showNewMessage?.(message);
  }, []);

  /*
   * AGI-428: the payload the detail renderer and the auto-open host read.
   * Published only while the source is RESOLVED: `loading` must not look like
   * "this workspace has no notice" to a consumer either, which is the same
   * R3b distinction the snapshot keeps, one layer out. The source itself is
   * already role- and flag-gated, so nothing else needs re-checking here.
   */
  const liveMetaPricingNotice =
    metaPricingNotice.status === 'ready' ? metaPricingNotice.value : null;

  return useMemo(
    () => ({
      alerts,
      derivedAlerts,
      wideAlert: wideArbitration.winner,
      waitingCriticalAlerts: wideArbitration.waiting,
      wideAlertShowsCta,
      badgeCount,
      areSurfacesEnabled,
      acknowledgeAlert,
      acknowledgedKeys,
      acknowledgedAt,
      alertSessionLog,
      loadingAlertIds,
      registerInlineAlert,
      inlineAlertIds,
      inlineAlertSources,
      sellerPauseReason,
      quotaExceededCount,
      metaPricingNotice: liveMetaPricingNotice,
      openSupport,
      reportBoardExpiredLead,
      requestStageThresholdEdit,
      stageThresholdEditRequest,
      consumeStageThresholdEditRequest,
      setPastDueZoneOnScreen,
    }),
    [
      alerts,
      derivedAlerts,
      wideArbitration,
      wideAlertShowsCta,
      badgeCount,
      areSurfacesEnabled,
      acknowledgeAlert,
      acknowledgedKeys,
      acknowledgedAt,
      alertSessionLog,
      loadingAlertIds,
      registerInlineAlert,
      inlineAlertIds,
      inlineAlertSources,
      sellerPauseReason,
      quotaExceededCount,
      liveMetaPricingNotice,
      openSupport,
      reportBoardExpiredLead,
      requestStageThresholdEdit,
      stageThresholdEditRequest,
      consumeStageThresholdEditRequest,
      setPastDueZoneOnScreen,
    ],
  );
}

/**
 * Two board signals are the same when every field a consumer can read matches.
 * Compared rather than stored blindly so a board republishing an unchanged
 * signal (every minute, while a card stays red) does not rebuild the snapshot
 * and re-run alert selection for nothing.
 */
function isSameBoardExpiredSignal(
  a: tBoardExpiredSignal,
  b: tBoardExpiredSignal,
): boolean {
  if (!a.hasExpired || !b.hasExpired) return a.hasExpired === b.hasExpired;

  return (
    a.stageId === b.stageId &&
    a.stageName === b.stageName &&
    a.leadName === b.leadName &&
    a.elapsedMinutes === b.elapsedMinutes
  );
}
